import React, { useCallback, useContext } from 'react'
import AdvancedMarker from './AdvancedMarker'
import PropertyPriceMarker from './PropertyPriceMarker'
import { format as formatCurrency } from 'lib/formatCurrencyIntl'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import PillMarker from './PillMarker'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
} from 'constants/offer'
import config from 'constants/config'
import { OfferBestPriceData } from 'selectors/offerSelectors'
import { useOptimizelyExperiment, OptimizelyClientSideExperiments } from 'lib/optimizely'
import { getLowestPricePackage } from 'lib/getLowestPricePackage'

const MAX_PIN_LIMIT = config.search.mapView.pinLimit

interface Props {
  map: google.maps.Map | null;
  markers: Array<App.SearchMapMarker>;
  onMarkerClick: (marker: App.SearchMapMarker, pinType: string) => void;
  bestPrices: Record<string, OfferBestPriceData>;
  activeMarker: string | null;
  visitedMarkers: Set<string> | undefined;
  filters: App.OfferListFilters;
}

function MarkerRenderer(props: Props) {
  const { map, markers, onMarkerClick, activeMarker, bestPrices, visitedMarkers, filters } = props

  const [hoveredMarker, setHoveredMarker] = React.useState<string | null>(null)
  const geo = useContext(GeoContext)
  const currencyCode = useContext(CurrencyContext)

  const hasDates = !!filters?.checkIn

  const showNightlyPrice = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.searchNightlyPriceMapMarkers)

  const calculateZIndex = useCallback(
    (marker: App.SearchMapMarker, index: number) => {
      if (activeMarker === marker.id || hoveredMarker === marker.id) {
        return MAX_PIN_LIMIT + 1
      }
      if (index >= MAX_PIN_LIMIT) {
        return 0
      }
      const offerType = marker.offers[0].type
      if (offerType === OFFER_TYPE_HOTEL || offerType === OFFER_TYPE_ALWAYS_ON || offerType === OFFER_TYPE_BED_BANK) {
        return MAX_PIN_LIMIT - index
      }
      return 0
    },
    [activeMarker, hoveredMarker],
  )

  const formatLabel = useCallback((marker: App.SearchMapMarker) => {
    const offer = marker.offers[0]
    const { loading, bestPrice } = bestPrices[offer.id]

    if (loading) {
      return null
    }

    const bedbankSellPrice =
      'sell' in offer && offer.type === OFFER_TYPE_BED_BANK ? offer.sell?.price : undefined
    const minimumDuration = 'minDuration' in offer ? offer.minDuration : undefined

    const lowestPrice =
      offer.type !== OFFER_TYPE_BED_BANK ?
        getLowestPricePackage(offer, offer.packages, true)?.price :
        bedbankSellPrice

    const totalPrice = (hasDates || !showNightlyPrice ? bestPrice : lowestPrice) ?? 0

    const displayedPrice =
      hasDates || !showNightlyPrice ? bestPrice : totalPrice / (minimumDuration ?? 1)

    const formattedPrice = formatCurrency(
      displayedPrice ?? 0,
      currencyCode,
      geo.currentRegionCode,
      'roundedDollar',
    )

    return displayedPrice ? formattedPrice : null
  }, [bestPrices, currencyCode, geo.currentRegionCode, hasDates, showNightlyPrice])

  return (
    <>
      {markers.map((marker, index) => (
        <AdvancedMarker
          key={marker.id}
          map={map}
          position={{
            lat: marker.latitude ?? 0,
            lng: marker.longitude ?? 0,
          }}
          onClick={() => onMarkerClick(marker, index < MAX_PIN_LIMIT ? 'pricePin' : 'pill')}
          zIndex={calculateZIndex(marker, index)}
        >
          {index < MAX_PIN_LIMIT && !bestPrices[marker.offers[0].id].loading && (
            <PropertyPriceMarker
              isHovered={hoveredMarker === marker.id}
              setIsHovered={setHoveredMarker}
              isVisited={!!visitedMarkers?.has(marker.id)}
              isActive={activeMarker === marker.id}
              marker={marker}
              formattedLabel={formatLabel(marker)}
            />
          )}
          {index >= MAX_PIN_LIMIT && !bestPrices[marker.offers[0].id].loading && (
            <PillMarker
              isHovered={hoveredMarker === marker.id}
              setIsHovered={setHoveredMarker}
              isVisited={!!visitedMarkers?.has(marker.id)}
              isActive={activeMarker === marker.id}
              marker={marker}
              formattedLabel={formatLabel(marker)}
            />
          )}
        </AdvancedMarker>
      ))}
    </>
  )
}

export default MarkerRenderer
